import { DateTime } from "luxon";
import { createContext, type FC, type ReactNode, type RefObject, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InformationalPopup } from "../components/InformationalPopup/InformationalPopup";
import { localStorageService } from "../services/LocalStorageService";
import { trackEvent } from "../utils/analytics";
import { useAuth } from "./AuthProvider";

export interface PopupConfig {
	id: string;
	elementRef: RefObject<HTMLElement | null>;
	title: string;
	content: ReactNode | string;
	imageUrl?: string;
	localStorageKey: string;
	delayBeforeShow?: number;
	positions?: ("top" | "bottom" | "left" | "right")[];
	align?: "start" | "center" | "end";
	shouldShow?: () => boolean;
	verticalOffset?: number;
}

interface PopupItem extends PopupConfig {
	isOpen: boolean;
}

interface PopupManagerContextType {
	registerPopup: (config: PopupConfig) => void;
	unregisterPopup: (id: string) => void;
	closePopup: (id: string) => void;
}

const PopupManagerContext = createContext<PopupManagerContextType | null>(null);

export const usePopupManager = () => {
	const context = useContext(PopupManagerContext);
	if (!context) {
		throw new Error("usePopupManager must be used within PopupManagerProvider");
	}
	return context;
};

interface PopupManagerProviderProps {
	children: ReactNode;
}

const POPUP_ORDER = ["master-schedule-name-update", "feedback-popover"];

export const PopupManagerProvider: FC<PopupManagerProviderProps> = ({ children }) => {
	const [popups, setPopups] = useState<Map<string, PopupItem>>(new Map());
	const [activePopupId, setActivePopupId] = useState<string | null>(null);
	const shownPopupsRef = useRef<Set<string>>(new Set());
	const delayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const {
		userInfo: { id: userId },
	} = useAuth();

	const registerPopup = useCallback(
		(config: PopupConfig) => {
			const isClosed = localStorageService.getPopupClosed(config.localStorageKey, userId);
			if (isClosed) {
				return;
			}

			if (config.shouldShow && !config.shouldShow()) {
				return;
			}

			const popupItem: PopupItem = {
				...config,
				isOpen: false,
			};

			setPopups((prev) => {
				const updatedPopups = new Map(prev);
				updatedPopups.set(config.id, popupItem);
				return updatedPopups;
			});
		},
		[userId],
	);

	const unregisterPopup = useCallback((id: string) => {
		setPopups((prev) => {
			const newMap = new Map(prev);
			newMap.delete(id);
			return newMap;
		});
	}, []);

	const showNextPopup = useCallback(() => {
		setPopups((currentPopups) => {
			const currentlyOpen = Array.from(currentPopups.values()).find((p) => p.isOpen);
			if (currentlyOpen) {
				return currentPopups;
			}

			const lastPopupDate = localStorageService.getLastPopupShownDate(userId);
			const today = DateTime.now().toISO();
			if (lastPopupDate && DateTime.fromISO(lastPopupDate).startOf("day") === DateTime.now().startOf("day")) {
				return currentPopups;
			}

			const nextPopup = POPUP_ORDER.map((id) => currentPopups.get(id)).find(
				(popup) => popup && !shownPopupsRef.current.has(popup.id) && !popup.isOpen && popup.elementRef.current,
			);

			if (!nextPopup) {
				return currentPopups;
			}
			const delay = nextPopup.delayBeforeShow || 0;

			if (delayTimeoutRef.current) {
				clearTimeout(delayTimeoutRef.current);
			}

			delayTimeoutRef.current = setTimeout(() => {
				setPopups((prev) => {
					const existing = prev.get(nextPopup.id);
					if (!existing || !existing.elementRef.current) {
						shownPopupsRef.current.add(nextPopup.id);
						const newMap = new Map(prev);
						newMap.delete(nextPopup.id);
						trackEvent("popup_show_failed", {
							popup_id: nextPopup.id,
							reason: "element_ref_missing",
						});
						return newMap;
					}
					localStorageService.setLastPopupShownDate(today, userId);

					const newMap = new Map(prev);
					newMap.set(nextPopup.id, { ...existing, isOpen: true });
					setActivePopupId(nextPopup.id);

					trackEvent("popup_shown", {
						popup_id: nextPopup.id,
						popup_title: nextPopup.title,
						delay_ms: delay,
						popup_order: POPUP_ORDER.indexOf(nextPopup.id),
					});

					return newMap;
				});
			}, delay);

			return currentPopups;
		});
	}, [userId]);

	const closePopup = useCallback(
		(id: string) => {
			setPopups((prev) => {
				const popup = prev.get(id);
				if (popup) {
					localStorageService.setPopupClosed(popup.localStorageKey, true, userId);
				}

				const newMap = new Map(prev);
				const existing = newMap.get(id);
				if (existing) {
					newMap.set(id, { ...existing, isOpen: false });
				}

				shownPopupsRef.current.add(id);
				setActivePopupId(null);

				return newMap;
			});
		},
		[userId],
	);

	useEffect(() => {
		if (activePopupId === null && popups.size > 0) {
			const timer = setTimeout(() => {
				showNextPopup();
			}, 300);
			return () => clearTimeout(timer);
		}
	}, [popups.size, activePopupId, showNextPopup]);

	const activePopup = activePopupId ? popups.get(activePopupId) : null;

	return (
		<PopupManagerContext.Provider value={{ registerPopup, unregisterPopup, closePopup }}>
			{children}
			{activePopup?.isOpen && activePopup.elementRef.current && (
				<PopupRenderer key={activePopup.id} popup={activePopup} onClose={() => closePopup(activePopup.id)} />
			)}
		</PopupManagerContext.Provider>
	);
};

const PopupRenderer: React.FC<{ popup: PopupItem; onClose: () => void }> = ({ popup, onClose }) => {
	const [position, setPosition] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

	useEffect(() => {
		if (!popup.elementRef.current || !popup.isOpen) {
			setPosition(null);
			return;
		}

		const updatePosition = () => {
			if (popup.elementRef.current) {
				const rect = popup.elementRef.current.getBoundingClientRect();
				setPosition({
					left: rect.left,
					top: rect.top,
					width: rect.width,
					height: rect.height,
				});
			}
		};

		updatePosition();
	}, [popup.elementRef, popup.isOpen]);

	if (!popup.elementRef.current || !popup.isOpen || !position) {
		return null;
	}

	const verticalOffset = popup.verticalOffset || 0;
	const anchorTop = position.top + verticalOffset;

	return createPortal(
		<div
			style={{
				position: "fixed",
				left: `${position.left}px`,
				top: `${anchorTop}px`,
				width: `${position.width}px`,
				height: `${position.height}px`,
				pointerEvents: "none",
				zIndex: 10000,
			}}
		>
			<InformationalPopup
				isOpen={popup.isOpen}
				title={popup.title}
				content={popup.content}
				imageUrl={popup.imageUrl}
				onClose={onClose}
				positions={popup.positions || ["bottom", "top", "right", "left"]}
				align={popup.align || "start"}
			>
				<div
					style={{
						width: "100%",
						height: "100%",
					}}
				/>
			</InformationalPopup>
		</div>,
		document.body,
	);
};
